import type { ICellRendererParams } from "ag-grid-community"
import { isBefore } from "date-fns"

import type { ColumnNamingId } from "~/components/planning/ProductTable/utils/columnUtils"
import type { PurchaseOrderTableColumn } from "~/constants/purchaseOrderTable"
import { createAverageColumn } from "~/utils/columnTemplates"
import { differenceInCalendarDaysUTC, startOfTodayUTC } from "~/utils/datetime"
import { MonthCoverField, type MonthCoverVariant } from "./MonthCoverField"
// Type-only import — erased at build time, so no runtime cycle with PurchaseOrderTableV2.
import type { RowDataType } from "./PurchaseOrderTableV2"

/** The raw per-row figures a table gathers before the shared cover math applies. */
export interface MonthCoverRawInputs {
  stockLevel: number
  /** Incoming order quantity arriving before `arrivalDate`. */
  incomingBeforeArrival: number
  salesL12M: number
  /** The order/buy quantity being planned for the row. */
  orderQuantity: number
  /** Available-to-Sell; undefined when the row doesn't report it. */
  ats: number | undefined
  leadtimeDays: number
  /** When the planned units arrive — demand before arrival covers today → this date. */
  arrivalDate: Date | undefined
}

/** Raw inputs plus the figures every Month cover variant derives from them. */
interface MonthCoverInputs extends MonthCoverRawInputs {
  /** Monthly sales (salesL12M / 12) — the denominator for every variant. */
  monthlySales: number
  monthsUntilArrival: number
  /** Monthly sales × months until arrival, capped at what's actually sellable so
   *  projected stock at arrival never goes negative. */
  demandBeforeArrival: number
  /** Stock at arrival incl. this order: current + incoming - demand + order quantity. */
  totalAtArrival: number
}

function deriveInputs(raw: MonthCoverRawInputs): MonthCoverInputs {
  const monthlySales = raw.salesL12M / 12
  const monthsUntilArrival = raw.arrivalDate
    ? Math.max(differenceInCalendarDaysUTC(raw.arrivalDate, startOfTodayUTC()), 0) / 30
    : 0
  const demandBeforeArrival = Math.min(
    monthlySales * monthsUntilArrival,
    raw.stockLevel + raw.incomingBeforeArrival,
  )
  return {
    ...raw,
    monthlySales,
    monthsUntilArrival,
    demandBeforeArrival,
    totalAtArrival:
      raw.stockLevel + raw.incomingBeforeArrival - demandBeforeArrival + raw.orderQuantity,
  }
}

function computeMonthCover(variant: MonthCoverVariant, i: MonthCoverInputs): number | null {
  if (i.monthlySales === 0) return null
  switch (variant) {
    case "current":
      // ATS-first: Available-to-Sell is gathered at the same granularity as the other
      // inputs, so the ratio stays consistent. Fall back to on-hand stock when no ATS.
      return (i.ats ?? i.stockLevel) / i.monthlySales
    case "withPO":
      return i.totalAtArrival / i.monthlySales
    case "withPOMinusLeadtime":
      return i.totalAtArrival / i.monthlySales - i.leadtimeDays / 30
  }
}

/**
 * Builds one of the three Month cover columns for any table that can gather
 * `MonthCoverRawInputs` off a row. The variant selects the formula (current / with PO /
 * with PO - leadtime); the cover math and hovercard wiring live here so tables only
 * supply the raw figures.
 */
export function createMonthCoverColumn<T>({
  colId,
  variant,
  atsAvailable,
  resolveRaw,
  headerTooltip,
}: {
  colId: ColumnNamingId
  variant: MonthCoverVariant
  /** Whether any row in the table reports ATS — gates the "missing ATS" warning so it only
   *  shows for tenants that actually use Available-to-Sell. */
  atsAvailable: boolean
  /** Gathers the raw figures off a row; `context` is the live ag-grid context, for tables
   *  whose quantities are edited in place. */
  resolveRaw: (row: T, context: unknown) => MonthCoverRawInputs
  /** Overrides the naming-registry tooltip. Used where the figure needs a caveat the shared
   *  definition cannot carry — e.g. a multi-warehouse table, where cover is an aggregate ratio
   *  and must not be read as any one warehouse's position. */
  headerTooltip?: string
}) {
  return createAverageColumn<T>({
    colId,
    ...(headerTooltip ? { headerTooltip } : {}),
    decimals: 1,
    hasTooltip: false,
    filter: true,
    valueGetter: (params) =>
      params.data
        ? computeMonthCover(variant, deriveInputs(resolveRaw(params.data, params.context)))
        : null,
    cellRenderer: (params: ICellRendererParams<T>) => {
      if (params.node.group) return params.valueFormatted ?? "-"
      if (!params.data) return null
      const i = deriveInputs(resolveRaw(params.data, params.context))
      return (
        <MonthCoverField
          variant={variant}
          value={(params.value as number | null | undefined) ?? null}
          salesL12M={i.salesL12M}
          stockLevel={i.stockLevel}
          availableToSell={i.ats}
          atsAvailable={atsAvailable}
          orderQuantity={i.orderQuantity}
          leadtimeDays={i.leadtimeDays}
          incomingStock={i.incomingBeforeArrival}
          demandBeforeArrival={i.demandBeforeArrival}
          monthsUntilArrival={i.monthsUntilArrival}
        />
      )
    },
  })
}

/** Per-row context the PO table's Month cover columns need but can't read off the row. */
interface MonthCoverContext {
  warehouseId: string
  companyTotalLeadTimeDays: number | null | undefined
  /** ETA of the order being edited — demand before arrival covers today → this date. */
  expectedArrivalDate: Date
  atsAvailable: boolean
}

function resolvePORaw(row: RowDataType, ctx: MonthCoverContext): MonthCoverRawInputs {
  const warehouse = row.perWarehouse.get(ctx.warehouseId)
  return {
    stockLevel: warehouse?.stockLevel ?? 0,
    incomingBeforeArrival: (warehouse?.incomingOrders ?? [])
      .filter((o) => isBefore(o.expectedArrivalDate, ctx.expectedArrivalDate))
      .reduce((sum, o) => sum + o.quantity, 0),
    salesL12M: row.salesQuantity,
    orderQuantity: row.quantity ?? 0,
    ats: warehouse?.availableToSell,
    leadtimeDays: row.settings?.totalLeadTimeDays ?? ctx.companyTotalLeadTimeDays ?? 0,
    arrivalDate: ctx.expectedArrivalDate,
  }
}

/** The PO table's Month cover columns: per-SKU inputs from the selected warehouse. */
export function createPOMonthCoverColumn(
  colId: PurchaseOrderTableColumn,
  variant: MonthCoverVariant,
  ctx: MonthCoverContext,
) {
  return createMonthCoverColumn<RowDataType>({
    colId,
    variant,
    atsAvailable: ctx.atsAvailable,
    resolveRaw: (row) => resolvePORaw(row, ctx),
  })
}
